import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import AppBarComponent from "../components/AppBarComponent";
import { apiUrl } from "../apiConfig";
import {
  Container,
  Paper,
  Button,
  Box,
  Typography,
  styled,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Upload, CloudUpload, Settings, ExpandMore } from "@mui/icons-material";

const Input = styled('input')({
  display: 'none',
});

const Home = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const customRateOptions = [
    { key: 'pix', label: 'Pix' },
    { key: 'debito', label: 'Débito' },
    { key: 'credito_vista', label: 'Crédito à vista' },
    { key: 'credito_parcelado', label: 'Crédito parcelado' }
  ];

  const [customRates, setCustomRates] = useState({
    pix: { min: 0.0, max: 0.0 },
    debito: { min: 1.23, max: 1.31 },
    credito_vista: { min: 3.00, max: 3.05 },
    credito_parcelado: { min: 3.23, max: 3.28 }
  });

  const handleRateChange = (parcelas, tipo, valor) => {
    const numericValue = parseFloat(valor) || 0;
    setCustomRates(prev => ({
      ...prev,
      [parcelas]: {
        ...prev[parcelas],
        [tipo]: numericValue
      }
    }));
  };

  const handleFileTypeChange = (event) => {
    setFileType(event.target.value);
    // Limpar dados quando trocar de operadora
    setSelectedFile(null);
    setAnalysisResult(null);
    if (uploadStatus) {
      setUploadStatus(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!fileType) {
      setUploadStatus({
        type: 'error',
        message: 'Por favor, selecione o tipo de arquivo antes de enviar.'
      });
      return;
    }

    setSelectedFile(file);
    setLoading(true);
    setUploadStatus(null);
    setAnalysisResult(null);

    try {
      if (!user || !user.id) {
        throw new Error("Você precisa estar logado para enviar arquivos.");
      }

      const formData = new FormData();
      formData.append('csvFile', file);
      formData.append('fileType', fileType);
      formData.append('userId', user.id);
      formData.append('customRates', JSON.stringify(customRates));

      const response = await fetch(apiUrl('/api/upload'), {
        method: 'POST',
        body: formData,
        headers: {
          'x-user': JSON.stringify(user)
        }
      });

      const result = await response.json();

      if (response.ok) {
        const hasAiError = Boolean(result.analysisError);
        setAnalysisResult(result.analysis || null);
        setUploadStatus({
          type: hasAiError ? 'warning' : 'success',
          message: hasAiError
            ? `Arquivo salvo, mas ocorreu um erro na análise com IA: ${result.analysisError}`
            : `Arquivo "${result.upload.filename}" enviado para análise. Confira o resultado abaixo.`
        });
      } else {
        setUploadStatus({
          type: 'error',
          message: `Erro ao salvar/analisar arquivo: ${result.error || 'Falha desconhecida'}`
        });
      }
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      setUploadStatus({
        type: 'error',
        message: `Erro ao processar arquivo: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const getOperadoraInfo = () => {
    switch (fileType) {
      case 'stone':
        return {
          name: 'Stone',
          format: 'CSV com separador ponto e vírgula (;)',
          description: 'Análise automatizada via Gemini (prompt único). Colunas esperadas: VALOR BRUTO, VALOR LIQUIDO, DESCONTO DE MDR, N DE PARCELAS'
        };
      case 'cielo':
        return {
          name: 'Cielo',
          format: 'CSV com separador ponto e vírgula (;)',
          description: 'Análise automatizada via Gemini (prompt único). Colunas esperadas: Data da venda, Forma de pagamento, Valor bruto, Taxa/tarifa, Valor líquido'
        };
      case 'american':
        return {
          name: 'American Express',
          format: 'CSV com separador ponto e vírgula (;)',
          description: 'Análise automatizada via Gemini (prompt único). Formato similar ao Stone'
        };
      case 'rede':
        return {
          name: 'Rede',
          format: 'CSV com separador ponto e vírgula (;)',
          description: 'Análise automatizada via Gemini procurando inconsistências e diferenças cobradas.'
        };
      case 'sipag':
        return {
          name: 'Sipag',
          format: 'CSV com separador ponto e vírgula (;)',
          description: 'Análise automatizada via Gemini procurando inconsistências e diferenças cobradas.'
        };
      default:
        return null;
    }
  };

  const operadoraInfo = getOperadoraInfo();

  return (
    <>
      <AppBarComponent />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography variant="h4" gutterBottom color="primary">
            Upload do Arquivo CSV
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Selecione a operadora e um arquivo CSV para validar os cálculos de MDR. O arquivo será salvo no servidor para consulta posterior e analisado via IA Gemini com o mesmo prompt para todas as operadoras.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <FormControl
              required
              sx={{
                minWidth: 200,
                flex: '1 1 auto',
                maxWidth: { xs: '100%', sm: '300px' }
              }}
            >
              <InputLabel
                id="file-type-label"
                sx={{ fontSize: '1.1rem' }}
              >
                Operadora
              </InputLabel>
              <Select
                labelId="file-type-label"
                id="file-type-select"
                value={fileType}
                label="Operadora"
                onChange={handleFileTypeChange}
                disabled={loading}
                sx={{
                  height: 56,
                  fontSize: '1.1rem',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    py: 1.5,
                  }
                }}
              >
                <MenuItem value=""><em>Selecione a operadora</em></MenuItem>
                <MenuItem value="stone">Stone</MenuItem>
                <MenuItem value="cielo">Cielo</MenuItem>
                <MenuItem value="american">American Express</MenuItem>
                <MenuItem value="rede">Rede</MenuItem>
                <MenuItem value="sipag">Sipag</MenuItem>
              </Select>
            </FormControl>

            <label htmlFor="csv-upload">
              <Input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={loading || !fileType}
              />
              <Button
                variant="contained"
                component="span"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                size="large"
                disabled={loading || !fileType}
                sx={{
                  px: 4,
                  py: 1.5,
                  height: 56,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(46, 91, 186, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(46, 91, 186, 0.4)',
                  },
                  flex: '1 1 auto',
                  maxWidth: { xs: '100%', sm: '300px' }
                }}
              >
                {loading ? 'Processando...' : 'Escolher Arquivo CSV'}
              </Button>
            </label>
          </Box>

          {/* Informações da operadora selecionada */}
          {operadoraInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Operadora selecionada: {operadoraInfo.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Formato: {operadoraInfo.format}
              </Typography>
              <Typography variant="body2">
                {operadoraInfo.description}
              </Typography>
            </Alert>
          )}

          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="custom-rates-content"
              id="custom-rates-header"
            >
              <Settings sx={{ mr: 1 }} />
              <Typography>Configurar Taxas Personalizadas</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {customRateOptions.map(({ key, label }) => (
                  <Grid item xs={12} sm={6} md={3} key={key}>
                    <Typography variant="subtitle2" gutterBottom>
                      {label}
                    </Typography>
                    <TextField
                      label="Taxa Mínima"
                      type="number"
                      value={customRates[key]?.min ?? ''}
                      onChange={(e) => handleRateChange(key, 'min', e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                      inputProps={{ step: "0.01" }}
                    />
                    <TextField
                      label="Taxa Máxima"
                      type="number"
                      value={customRates[key]?.max ?? ''}
                      onChange={(e) => handleRateChange(key, 'max', e.target.value)}
                      size="small"
                      fullWidth
                      inputProps={{ step: "0.01" }}
                    />
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {uploadStatus && (
            <Alert 
              severity={uploadStatus.type} 
              sx={{ mt: 2 }}
              icon={uploadStatus.type === 'success' ? <CloudUpload /> : undefined}
            >
              {uploadStatus.message}
            </Alert>
          )}

          {selectedFile && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Arquivo selecionado:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                {operadoraInfo && (
                  <span> - <strong>Operadora:</strong> {operadoraInfo.name}</span>
                )}
              </Typography>
            </Box>
          )}
        </Paper>

        {analysisResult && (
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              mt: 3
            }}
          >
            <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 2 }}>
              Análise IA - {operadoraInfo?.name || 'Operadora'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Resultado gerado pela Gemini com o CSV enviado.
            </Typography>
            <Box
              sx={{
                borderRadius: 2,
                p: 2,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              {analysisResult}
            </Box>
          </Paper>
        )}
      </Container>
    </>
  );
};

export default Home;
